/**
 * ReplayControls Component
 *
 * Playback controls for the writing replay feature.
 * Provides Previous, Play/Pause, Next buttons and a speed selector.
 *
 * @example
 * ```tsx
 * <ReplayControls
 *   isPlaying={isPlaying}
 *   onPlayPause={togglePlayPause}
 *   onPrevious={goToPrevious}
 *   onNext={goToNext}
 *   canGoPrevious={currentIndex > 0}
 *   canGoNext={currentIndex < totalMoments - 1}
 *   speed={playbackSpeed}
 *   onSpeedChange={setPlaybackSpeed}
 * />
 * ```
 */

import { useRef } from "react";
import PiPlayBigSolid from "@markeroo/icons/src/solid/media/PiPlayBigSolid";
import PiPauseSquareSolid from "@markeroo/icons/src/solid/media/PiPauseSquareSolid";
import PiChevronLeftStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronLeftStroke";
import PiChevronRightStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronRightStroke";
import { buttonVariants } from "@markeroo/theme";

/** Playback speed options for replay */
export type PlaybackSpeed = 0.5 | 1 | 2 | 5;

/** Speed configuration for display and timing */
export const SPEED_CONFIG: Record<
  PlaybackSpeed,
  { label: string; delayMs: number }
> = {
  0.5: { label: "0.5x", delayMs: 2000 },
  1: { label: "1x", delayMs: 1000 },
  2: { label: "2x", delayMs: 500 },
  5: { label: "5x", delayMs: 200 },
};

/**
 * Strings for the ReplayControls component.
 * These can be overridden via props for i18n.
 */
export interface ReplayControlsStrings {
  playbackControls: string;
  stepBack: string;
  stepForward: string;
  play: string;
  pause: string;
  speed: string;
  playbackSpeed: string;
}

const defaultStrings: ReplayControlsStrings = {
  playbackControls: "Playback Controls",
  stepBack: "Step back",
  stepForward: "Step forward",
  play: "Play",
  pause: "Pause",
  speed: "Speed",
  playbackSpeed: "Playback speed",
};

export interface ReplayControlsProps {
  /** Is replay currently playing */
  isPlaying: boolean;
  /** Toggle play/pause */
  onPlayPause: () => void;
  /** Go to previous moment/edit */
  onPrevious: () => void;
  /** Go to next moment/edit */
  onNext: () => void;
  /** Can go to previous (disable at start) */
  canGoPrevious: boolean;
  /** Can go to next (disable at end) */
  canGoNext: boolean;
  /** Current playback speed */
  speed: PlaybackSpeed;
  /** Change playback speed */
  onSpeedChange: (speed: PlaybackSpeed) => void;
  /** Optional className */
  className?: string;
  /** Test ID for the component */
  testId?: string;
  /** Override strings for i18n */
  strings?: Partial<ReplayControlsStrings>;
  /** Custom Select component (for app integration) */
  SelectComponent?: React.ComponentType<{
    id: string;
    value: number;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    className?: string;
    "aria-label"?: string;
    "data-testid"?: string;
    children: React.ReactNode;
  }>;
}

/**
 * Default select component using native HTML select.
 */
function DefaultSelect({
  id,
  value,
  onChange,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  children,
}: {
  id: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  children: React.ReactNode;
}) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- DefaultSelect is a fallback component for the shared package
    <select
      id={id}
      value={value}
      onChange={onChange}
      className={`rounded border border-border bg-surface-primary px-2 py-1.5 text-sm text-text-theme-primary focus:outline-none focus:ring-2 focus:ring-brand-500 ${className}`}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {children}
    </select>
  );
}

export function ReplayControls({
  isPlaying,
  onPlayPause,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  speed,
  onSpeedChange,
  className = "",
  testId = "replay-controls",
  strings: stringOverrides,
  SelectComponent = DefaultSelect,
}: ReplayControlsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const strings = { ...defaultStrings, ...stringOverrides };

  // Keyboard shortcuts are handled by ReplayContainer to avoid duplication.
  // This component only handles button clicks and the speed select.

  // Handle speed select change
  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSpeed = parseFloat(e.target.value) as PlaybackSpeed;
    onSpeedChange(newSpeed);
  };

  return (
    <div
      ref={containerRef}
      className={`flex items-center justify-center gap-4 ${className}`}
      data-testid={testId}
      role="region"
      aria-label={strings.playbackControls}
    >
      {/* Transport Controls */}
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          type="button"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className={`${buttonVariants.ghost} p-3 disabled:opacity-50`}
          aria-label={strings.stepBack}
          data-testid={`${testId}-previous`}
        >
          <PiChevronLeftStroke size={24} />
        </button>

        {/* Play/Pause Button */}
        <button
          type="button"
          onClick={onPlayPause}
          className={`${buttonVariants.primary} p-4`}
          aria-label={isPlaying ? strings.pause : strings.play}
          data-testid={`${testId}-play-pause`}
        >
          {isPlaying ? (
            <PiPauseSquareSolid size={28} />
          ) : (
            <PiPlayBigSolid size={28} />
          )}
        </button>

        {/* Next Button */}
        <button
          type="button"
          onClick={onNext}
          disabled={!canGoNext}
          className={`${buttonVariants.ghost} p-3 disabled:opacity-50`}
          aria-label={strings.stepForward}
          data-testid={`${testId}-next`}
        >
          <PiChevronRightStroke size={24} />
        </button>
      </div>

      {/* Speed Selector - inline */}
      <div className="flex items-center gap-2">
        <label
          htmlFor="speed-select"
          className="text-sm text-text-theme-secondary"
        >
          {/* eslint-disable-next-line no-restricted-syntax -- punctuation colon */}
          {strings.speed}:
        </label>
        <SelectComponent
          id="speed-select"
          value={speed}
          onChange={handleSpeedChange}
          className="w-20 py-1.5 text-sm"
          aria-label={strings.playbackSpeed}
          data-testid={`${testId}-speed-select`}
        >
          {Object.entries(SPEED_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>
              {config.label}
            </option>
          ))}
        </SelectComponent>
      </div>

      {/* Screen reader announcement for playback state */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isPlaying
          ? `Playing at ${SPEED_CONFIG[speed].label} speed`
          : "Paused"}
      </div>
    </div>
  );
}

export default ReplayControls;
